import type { HttpErrorResponse } from '@angular/common/http';

import type { PaginationMeta } from '@core/http-types';
import type { EntityState, PickActions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
} from '@core/ngrx-legacy';
import type { ViewFilterDef } from '@recruitee/views-types';

export interface FilterGroupResponse {
  filters: ViewFilterDef[];
  meta: PaginationMeta;
}

export type FilterGroupId = `${string}:${string}`;
export interface FilterGroupState extends FilterGroupResponse {
  id: FilterGroupId;
}

export const getFilterGroupId = (
  dataSourceId: string,
  filterGroupId: string,
): `${string}:${string}` => `${dataSourceId}:${filterGroupId}}`;
const idSelector = (entity: { id: string }) => entity.id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<FilterGroupState>()(
  'ViewFilterGroup',
  {
    idSelector,

    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetch: (
          state: EntityState<any>,
          payload: {
            dataSourceId: string;
            filterGroupId: string;
            query?: { searchQuery?: string; page?: number; limit?: number };
          },
        ) => {
          const id = getFilterGroupId(payload.dataSourceId, payload.filterGroupId);
          return pipeState(
            state,
            next => adapter.removeOne(next, id),
            next => adapter.markAsPending(next, { scope: null, entities: [id] }),
          );
        },
        fetchSuccess: (state, payload: { data: FilterGroupState }) =>
          defaultActions.fetchOneSuccess(state, payload),
        fetchFailed: (state, payload: { error: HttpErrorResponse; id: FilterGroupId }) =>
          defaultActions.fetchOneFailed(state, payload),

        fetchMore: (
          state: EntityState<any>,
          payload: {
            dataSourceId: string;
            filterGroupId: string;
            query?: { searchQuery?: string; page?: number; limit?: number };
          },
        ) =>
          adapter.markAsPending(state, {
            scope: null,
            entities: [getFilterGroupId(payload.dataSourceId, payload.filterGroupId)],
          }),
        fetchMoreSuccess: (state, payload: { data: FilterGroupState }) => {
          const currentData = state.entityMap[payload.data.id]!;
          return defaultActions.fetchOneSuccess(state, {
            data: {
              ...payload.data,
              filters: [...currentData.filters, ...payload.data.filters],
            },
          });
        },
        fetchMoreFailed: (state, payload: { error: HttpErrorResponse; id: FilterGroupId }) =>
          defaultActions.fetchOneFailed(state, payload),
      };
    },
  },
);
export const ViewFilterGroupActions: PickActions<typeof actions, 'fetch' | 'fetchMore'> = actions;
export {
  reducer as ViewFilterGroupReducer,
  namespace as ViewFilterGroupNamespace,
  selectors as ViewFilterGroupSelectors,
};
